import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { getCityBySlug, CITIES } from "../lib/data/cities";
import { SERVICES } from "../lib/data/services";
import { setSEO } from "../lib/data/seo";
import { Reveal, RevealLines } from "../components/motion/Reveal";
import SpotlightImage from "../components/site/SpotlightImage";
import EditorialMarquee from "../components/site/EditorialMarquee";
import ContactBlock from "../components/site/ContactBlock";
import ReviewsStrip from "../components/site/ReviewsStrip";

export default function CityDetail() {
  const { slug } = useParams();
  const city = getCityBySlug(slug);

  useEffect(() => {
    if (!city) return;
    setSEO({
      title: `Security Services in ${city.name}, BC — Mobile Patrol, Guards, Concierge`,
      description: `Licensed security guards and mobile patrol in ${city.name}, ${city.region}. 24/7 dispatch, all 15+ services including construction, retail, event, mining, and residential security across ${city.name} and surrounding areas.`,
      keywords: (city.keywords || []).concat([
        `security ${city.name} BC`,
        `security guards ${city.name}`,
        `mobile patrol ${city.name}`,
        `${city.name} security company`,
      ]),
      canonical: `${window.location.origin}/cities/${city.slug}`,
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: `British Columbia Security Services — ${city.name}`,
        areaServed: `${city.name}, British Columbia`,
        description: city.intro,
      },
    });
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [city]);

  if (!city) return <Navigate to="/cities" replace />;

  const nearby = CITIES.filter((c) => c.region === city.region && c.slug !== city.slug).slice(0, 6);

  return (
    <div className="pt-24">
      {/* HERO */}
      <section className="pt-12 md:pt-20 pb-16 bg-[#F4F4F0]" data-testid="city-hero">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end">
          <div className="md:col-span-7">
            <Link to="/cities" className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
              ← All Cities
            </Link>
            <RevealLines
              lines={[`Security in`, `${city.name}.`]}
              className="mt-6 font-display text-6xl md:text-8xl lg:text-[10rem] tracking-tighter leading-[0.88] text-[#060913]"
            />
            <div className="mt-6 flex flex-wrap items-center gap-x-8 gap-y-2 font-mono text-[10px] tracking-[0.28em] uppercase text-[#5C5F66]">
              <span>Region · {city.region}</span>
              {city.population && <span>Population · {city.population}</span>}
              <span className="text-[#D92323]">24/7 Dispatch</span>
            </div>
            <p className="mt-8 max-w-xl text-lg md:text-xl text-[#5C5F66] leading-relaxed">
              {city.intro}
            </p>
          </div>
          <div className="md:col-span-5">
            <SpotlightImage
              src={city.image}
              alt={`${city.name}, British Columbia`}
              caption={`${city.name} · ${city.region}`}
              className="aspect-[4/5]"
            />
          </div>
        </div>
      </section>

      <EditorialMarquee items={[city.name.toUpperCase(), "BRITISH COLUMBIA", "24/7 DISPATCH", "LICENSED · BONDED · INSURED"]} />

      {/* HIGHLIGHTS */}
      {city.highlights && (
        <section className="bg-white border-y border-black/10 py-24" data-testid="city-highlights">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-4">
              <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
                Local Focus
              </div>
              <h2 className="mt-4 font-display text-4xl md:text-5xl leading-[0.95] tracking-tight text-[#060913]">
                What {city.name} actually needs.
              </h2>
            </div>
            <div className="md:col-span-8">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-0 border-t border-black/10">
                {city.highlights.map((h, i) => (
                  <li key={i} className="p-6 border-b border-r border-black/10 last:border-r-0">
                    <div className="font-mono text-[10px] tracking-[0.22em] text-[#D92323]">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="mt-2 text-xl text-[#060913]">{h}</div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* SERVICES × CITY MATRIX */}
      <section className="bg-[#F4F4F0] py-24 md:py-32" data-testid="city-services">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
            Services in {city.name}
          </div>
          <h2 className="mt-4 font-display text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[0.95] text-[#060913] max-w-4xl">
            All 15+ services, delivered locally in {city.name}.
          </h2>

          <div className="mt-14 border-t border-black/10">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.02}>
                <Link
                  to={`/${s.slug}-${city.slug}`}
                  data-testid={`city-service-link-${s.slug}`}
                  className="group grid grid-cols-12 gap-4 items-baseline py-6 border-b border-black/10 hover:bg-white transition-colors"
                >
                  <span className="col-span-1 font-mono text-[11px] tracking-[0.22em] text-black/40">
                    {s.number}
                  </span>
                  <span className="col-span-7 md:col-span-5 font-display text-2xl md:text-4xl leading-tight text-[#060913] group-hover:text-[#D92323] transition-colors">
                    {s.name}
                  </span>
                  <span className="hidden md:block col-span-5 text-[#5C5F66] text-sm">
                    {s.name} in {city.name}, {city.region}
                  </span>
                  <span className="col-span-4 md:col-span-1 text-right font-mono text-[11px] tracking-[0.22em] uppercase text-black/50 group-hover:text-[#D92323]">
                    →
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NEIGHBORHOODS / NEARBY */}
      {(city.neighborhoods || nearby.length > 0) && (
        <section className="bg-white border-y border-black/10 py-20" data-testid="city-nearby">
          <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-12">
            {city.neighborhoods && (
              <div>
                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
                  Neighborhoods We Cover
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {city.neighborhoods.map((n) => (
                    <span
                      key={n}
                      className="font-mono text-[11px] tracking-[0.18em] uppercase border border-black/20 px-3 py-1.5"
                    >
                      {n}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {nearby.length > 0 && (
              <div>
                <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
                  Nearby Cities
                </div>
                <ul className="mt-4 grid grid-cols-2 gap-2">
                  {nearby.map((n) => (
                    <li key={n.slug}>
                      <Link
                        to={`/cities/${n.slug}`}
                        className="font-display text-xl text-[#060913] hover:text-[#D92323]"
                      >
                        {n.name} →
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {/* CTA + FORM */}
      <ReviewsStrip cityName={city.name} />

      <section className="bg-[#060913] text-white py-24 md:py-32" data-testid="city-quote">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#D92323]">
              Next Step · {city.name}
            </div>
            <h2 className="mt-4 font-display text-4xl md:text-6xl tracking-tight leading-[0.95]">
              Get a quote for {city.name}.
            </h2>
            <p className="mt-6 text-white/70 text-base leading-relaxed max-w-md">
              Call or email — one of our {city.name}-area coordinators will
              answer your inquiry the same business day.
            </p>
          </div>
          <div className="md:col-span-6">
            <ContactBlock variant="dark" testid={`city-${city.slug}-contact`} />
          </div>
        </div>
      </section>
    </div>
  );
}
